import ts from 'typescript';
import { EvaluateExpressionError } from '../errors';
import type { EvaluateResponse, Evaluator } from '../types';

const evaluateExpression: Evaluator<ts.Expression | ts.LeftHandSideExpression> = ({
  node,
  variables,
  log,
  evaluators,
}) => {
  if (ts.isNumericLiteral(node)) {
    return evaluators.numericLiteral({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isStringLiteral(node)) {
    return evaluators.stringLiteral({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isIdentifier(node)) {
    return evaluators.identifier({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isBinaryExpression(node)) {
    return evaluators.binaryExpression({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isPropertyAccessExpression(node)) {
    return evaluators.propertyAccessExpression({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isPrefixUnaryExpression(node)) {
    return evaluators.prefixUnaryExpression({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isParenthesizedExpression(node)) {
    return evaluators.parenthesizedExpression({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isConditionalExpression(node)) {
    return evaluators.conditionalExpression({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isElementAccessExpression(node)) {
    return evaluators.elementAccessExpression({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isCallExpression(node)) {
    return evaluators.callExpression({
      evaluators,
      log,
      node,
      variables,
    });
  }

  if (ts.isObjectLiteralExpression(node)) {
    const obj: Record<string, EvaluateResponse> = {};
    node.properties.forEach((property, propertyIndex) => {
      let key: string | undefined;
      if (property.name) {
        if (ts.isIdentifier(property.name)) {
          key = property.name.escapedText.toString();
        } else if (ts.isStringLiteral(property.name)) {
          key = property.name.text;
        }
      }

      if (!key) {
        const noKeyError = new EvaluateExpressionError('Failed to extract key from property', { kind: property.kind });
        log.error(noKeyError, 'evaluate-expression-error');
        log.info({ node, propertyIndex, variables }, 'evaluate-expression-error');
        return;
      }

      let value: EvaluateResponse;
      if ('initializer' in property && property.initializer) {
        value = evaluators.expression({
          evaluators,
          log,
          node: property.initializer,
          variables,
        });

        if (value instanceof EvaluateExpressionError) {
          log.error(value, 'evaluate-expression-error');
          log.info({ node, propertyIndex, variables }, 'evaluate-expression-error');
        } else {
          obj[key] = value;
        }
      }
    });

    return obj;
  }

  if (ts.isArrayLiteralExpression(node)) {
    const arr = node.elements.map((element, elementIndex): EvaluateResponse => {
      const value = evaluators.expression({
        evaluators,
        log,
        node: element,
        variables,
      });

      if (value instanceof EvaluateExpressionError) {
        log.error(value, 'evaluate-expression-error');
        log.info({ elementIndex, node, variables }, 'evaluate-expression-error');
        return undefined;
      }

      return value;
    });
    return arr;
  }

  if (
    node
    && typeof node === 'object'
    && 'kind' in node
  ) {
    if (node.kind === ts.SyntaxKind.TrueKeyword) {
      return true;
    }
    if (node.kind === ts.SyntaxKind.FalseKeyword) {
      return false;
    }
    if (node.kind === ts.SyntaxKind.NullKeyword) {
      return null;
    }
  }

  const error = new EvaluateExpressionError('Unsupported Expression', { kind: node.kind });
  log.error(error, 'evaluate-expression-error');
  log.info({ node, variables }, 'evaluate-expression-error');

  return error;
};

export default evaluateExpression;
